import type { EstadoPosSentenca, PrazoEsclarecimento } from '../types/encerramento.types';

/** Status do processo que a faixa recebe — o mesmo vocabulário do detalhe e do painel. */
type StatusDoProcesso = string;

/**
 * A faixa pós-sentença só aparece quando tem algo VERDADEIRO a afirmar.
 *
 * Duas condições, e as duas por incidente:
 *
 * 1. **Depois da sentença.** Ela nasceu renderizada em todo processo, com `estado="aguardando"`
 *    fixo e prazo de `MOCK_INTIMACAO_SENTENCA` — um processo em triagem exibia "aguardando o prazo
 *    de esclarecimento" com data que não era dele.
 * 2. **Com prazo em mão, quando o estado é `aguardando`.** `montarConteudo` faz `if (!prazo) return
 *    null` DEPOIS dos ramos `encerrado`/`manifestacaoRecebida`, e o `null` cai no banner
 *    _"Prazo de esclarecimento não registrado — o controle deste processo não está no sistema"_.
 *    Sem prazo, todo processo em `SENTENCA_EMITIDA` passava a afirmar isso — e a frase é **falsa**:
 *    o prazo está no sistema, é o guard do trânsito que o calcula e é dele que a fila do Painel de
 *    Prazos vive. Num produto sob responsabilidade jurídica, isso é pior que a data de mock que
 *    esta frente removeu (COR-01 da review do #971).
 *
 * O painel do CLIENTE não tem porta para o prazo — o guard vive em `/admin/*`. Enquanto não tiver,
 * a faixa aparece só no estado `encerrado`, que é afirmável sem prazo. Qual banner a tela deve
 * mostrar pós-sentença sem prazo é decisão da designer, não minha.
 *
 * @example
 * if (!podeAfirmarEstadoPosSentenca(processo.status, prazo)) return null;
 */
export function podeAfirmarEstadoPosSentenca(
  status: StatusDoProcesso,
  prazo: PrazoEsclarecimento | null,
): boolean {
  if (status === 'CONCLUIDO') return true;
  return status === 'SENTENCA_EMITIDA' && prazo !== null;
}

/**
 * Estado da faixa derivado do processo REAL.
 *
 * `manifestacaoRecebida` e `prazoVencido` ficam de fora de propósito: quem sabe se houve pedido de
 * esclarecimento e se o prazo venceu é o guard do trânsito, e ele vive em `/admin/*` — o painel do
 * cliente não tem essa porta. Enquanto não tiver, a faixa diz o que é verificável aqui (aguardando
 * ou encerrado) em vez de afirmar um estado que não pode confirmar.
 */
export function estadoPosSentencaDoProcesso(
  status: StatusDoProcesso,
  dataEncerramento: string | null,
): EstadoPosSentenca {
  if (status === 'CONCLUIDO' || dataEncerramento) return 'encerrado';
  return 'aguardando';
}

/**
 * O processo já tem sentença — é o que torna o Certificado de Acessos da Sentença alcançável.
 * `CONCLUIDO` entra porque o encerramento vem depois dela. Um processo concluído sem sentença
 * (raro) abre a página no estado "sentença não proferida", que é a resposta certa.
 *
 * @example if (sentencaFoiProferida(processo.status)) mostrarCertificadoDaSentenca();
 */
export function sentencaFoiProferida(status: StatusDoProcesso): boolean {
  return status === 'SENTENCA_EMITIDA' || status === 'CONCLUIDO';
}
